import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Space, Table, Tag, Spin, Typography, Row, Col,
  Modal, message, Tooltip, Empty, Badge, Descriptions, Divider, Alert,
} from 'antd';
import {
  FileTextOutlined, EyeOutlined, ReloadOutlined,
  DownloadOutlined, PrinterOutlined, CheckCircleOutlined,
  FilePdfOutlined, SafetyOutlined, AuditOutlined,
  ExclamationCircleOutlined, EditOutlined,
} from '@ant-design/icons';
import {
  previewAgreement,
  confirmAgreement,
  regenerateAgreement,
  listAgreements,
  startAgreementESign,
  finalizeAgreementESign,
} from '../api';
import dayjs from 'dayjs';
import useESignWatcher from '../../../hooks/useESignWatcher';

const { Text, Title } = Typography;

const TYPE_LABELS = {
  PRE_LOAN_TAKEOVER: 'Pre-Loan Takeover & Advance Auth',
  FINAL_PURCHASE: 'Gold Purchase & Loan Takeover',
};

const TYPE_COLORS = {
  PRE_LOAN_TAKEOVER: 'blue',
  FINAL_PURCHASE: 'green',
};

/**
 * Statuses during which PRE_LOAN is allowed.
 */
const PRE_LOAN_ALLOWED = [
  'DOCS_VERIFIED',
  'ADVANCE_APPROVED',
  'PART_PAYMENT_DONE',
  'FULLY_PAID_AWAITING_RELEASE',
];

/**
 * Statuses during which FINAL_PURCHASE is allowed.
 */
const FINAL_PURCHASE_ALLOWED = [
  'VALUATION_COMPLETED',
  'PAYMENT_SETTLED',
  'BILL_GENERATED',
  'CLOSED',
];

const GoldAgreementSection = ({ caseId, caseData, goldReceipt, onAgreementCreated, filterType }) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();

  // State
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(false);

  // Preview modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [regenerating, setRegenerating] = useState(null); // agreementId being regenerated

  // eSign state
  const [eSignLoading, setESignLoading] = useState(false);
  const [eSignModal, setESignModal] = useState({
    visible: false,
    agreementId: null,
    signUrl: null,
    requestId: null,
    documentId: null,
  });

  // ── Load agreements for this case ──
  // silent: refresh in place (eSign signals) without the table spinner.
  const loadAgreements = useCallback(async ({ silent = false } = {}) => {
    if (!caseId) return;
    if (!silent) setLoading(true);
    try {
      const result = await listAgreements(caseId, { limit: 50 });
      setAgreements(result.data || []);
    } catch (err) {
      if (err?.response?.status !== 404) {
        console.error('Load agreements error:', err);
      }
      if (!silent) setAgreements([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    loadAgreements();
  }, [loadAgreements]);

  // Webhook / signer return settles the agreement server-side and pushes
  // `esign.updated`; we re-read the list. No "Signing Complete" click needed.
  const eSignWatcher = useESignWatcher({
    documentId: eSignModal.documentId,
    active: eSignModal.visible,
    onSignal: () => loadAgreements({ silent: true }),
  });
  const watchedAgreement = agreements.find((a) => a.agreementId === eSignModal.agreementId);
  const watchedStatus = watchedAgreement?.esign?.documentId === eSignModal.documentId ? watchedAgreement?.esign?.status : null;

  useEffect(() => {
    if (!eSignModal.visible || watchedStatus !== 'completed') return;
    eSignWatcher.closePopup();
    setESignModal({ visible: false, agreementId: null, signUrl: null, requestId: null, documentId: null });
    messageApi.success('Agreement signed — signed copy saved.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedStatus, eSignModal.visible]);

  // ── Derived flags ──
  const status = caseData?.status || '';
  const canPreLoan = PRE_LOAN_ALLOWED.includes(status);
  const canFinalPurchase = FINAL_PURCHASE_ALLOWED.includes(status) && !!goldReceipt;

  const existingPreLoan = agreements.find((a) => a.agreementType === 'PRE_LOAN_TAKEOVER');
  const existingFinalPurchase = agreements.find((a) => a.agreementType === 'FINAL_PURCHASE');

  // ── Preview ──
  const handlePreview = async (agreementType) => {
    setPreviewing(true);
    setPreviewType(agreementType);
    try {
      const result = await previewAgreement(caseId, agreementType);
      setPreviewPdf(result.pdfBase64);
      setPreviewOpen(true);
    } catch (err) {
      console.error('Preview error:', err);
      messageApi.error(err?.response?.data?.message || 'Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  // ── Confirm ──
  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const result = await confirmAgreement(caseId, previewType);
      messageApi.success(`Agreement ${result.data?.agreementId || ''} confirmed & saved`);
      setPreviewOpen(false);
      setPreviewPdf(null);
      loadAgreements();
      onAgreementCreated?.(result.data);
    } catch (err) {
      console.error('Confirm error:', err);
      messageApi.error(err?.response?.data?.message || 'Failed to confirm agreement');
    } finally {
      setConfirming(false);
    }
  };

  // ── Regenerate ──
  const handleRegenerate = (agreementId) => {
    modal.confirm({
      title: 'Regenerate Agreement PDF?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <Text>
          This will re-generate the PDF from the <strong>original snapshot data</strong> (no new data will be fetched).
          The previous PDF will be saved in the regeneration history. Continue?
        </Text>
      ),
      okText: 'Regenerate',
      okType: 'primary',
      onOk: async () => {
        setRegenerating(agreementId);
        try {
          const result = await regenerateAgreement(agreementId);
          messageApi.success(`Agreement regenerated (v${result.data?.version || '?'})`);
          loadAgreements();
        } catch (err) {
          console.error('Regenerate error:', err);
          messageApi.error(err?.response?.data?.message || 'Failed to regenerate');
        } finally {
          setRegenerating(null);
        }
      },
    });
  };

  // ── eSign Start ──
  const handleESignStart = async (agreementId) => {
    setESignLoading(true);
    try {
      messageApi.loading('Starting eSign process...');
      const result = await startAgreementESign(agreementId);

      const signUrl = result?.data?.signUrl;
      const requestId = result?.data?.requestId;
      const documentId = result?.data?.documentId;

      if (!signUrl) {
        throw new Error('Failed to get signing URL');
      }

      messageApi.destroy();
      messageApi.success(result?.messages?.[0]?.message || 'eSign request created!');

      setESignModal({
        visible: true,
        agreementId,
        signUrl,
        requestId,
        documentId,
      });
      eSignWatcher.open(signUrl); // may be popup-blocked after the await; the modal has a button
    } catch (err) {
      messageApi.destroy();
      messageApi.error('Failed to start eSign: ' + (err?.response?.data?.message || err?.message || err));
    } finally {
      setESignLoading(false);
    }
  };

  // ── eSign Finalize ──
  const handleESignFinalize = async (agreementIdArg) => {
    const targetId = typeof agreementIdArg === 'string' ? agreementIdArg : eSignModal.agreementId;
    if (!targetId) return;
    setESignLoading(true);
    try {
      messageApi.loading('Finalizing eSign...');
      const result = await finalizeAgreementESign(targetId);

      messageApi.destroy();
      if (result.success) {
        messageApi.success('Agreement signed successfully!');
        eSignWatcher.closePopup();
        setESignModal({ visible: false, agreementId: null, signUrl: null, requestId: null, documentId: null });
        loadAgreements({ silent: true });
      } else {
        throw new Error(result.message || 'Failed to finalize');
      }
    } catch (err) {
      messageApi.destroy();
      messageApi.error('Failed to finalize eSign: ' + (err?.response?.data?.message || err?.message || err));
    } finally {
      setESignLoading(false);
    }
  };

  const handleESignCancel = () => {
    eSignWatcher.closePopup();
    setESignModal({ visible: false, agreementId: null, signUrl: null, requestId: null, documentId: null });
  };

  // ── Table columns ──
  const columns = [
    {
      title: 'Agreement ID',
      dataIndex: 'agreementId',
      key: 'agreementId',
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: 'Type',
      dataIndex: 'agreementType',
      key: 'agreementType',
      render: (v) => (
        <Tag color={TYPE_COLORS[v] || 'default'}>
          {TYPE_LABELS[v] || v}
        </Tag>
      ),
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      render: (v) => <Badge count={`v${v}`} style={{ backgroundColor: '#52c41a' }} />,
    },
    {
      title: 'Generated By',
      key: 'generatedBy',
      render: (_, r) => r.generatedBy?.name || '-',
    },
    {
      title: 'Generated At',
      dataIndex: 'generatedAt',
      key: 'generatedAt',
      render: (v) => v ? dayjs(v).format('DD MMM YYYY, hh:mm A') : '-',
    },
    {
      title: 'eSign',
      key: 'esign',
      render: (_, record) => {
        const status = record.esign?.status;
        if (status === 'completed') {
          return <Tag color="green">Signed</Tag>;
        }
        // Only a row that actually carries a signUrl is genuinely pending —
        // 'started' without one means the provider rejected the request.
        if (status === 'started' && record.esign?.signUrl) {
          return <Tag color="orange">Pending</Tag>;
        }
        if (status === 'failed' || status === 'started') {
          return (
            <Tooltip title={record.esign?.metadata?.error || 'The eSign provider did not return a signing link.'}>
              <Tag color="red">Failed</Tag>
            </Tooltip>
          );
        }
        return <Tag color="default">Not signed</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={record.esign?.signedDocumentUrl ? 'View / Download the signed agreement' : 'View / Download PDF'}>
            <Button
              type="link"
              size="small"
              icon={<FilePdfOutlined />}
              onClick={() => {
                // Prefer the signed copy once we hold it — record.pdfUrl is the
                // unsigned original and stays available under "Original".
                const url = record.esign?.signedDocumentUrl || record.pdfUrl;
                if (url) window.open(url, '_blank');
              }}
              disabled={!record.pdfUrl && !record.esign?.signedDocumentUrl}
            >
              {record.esign?.signedDocumentUrl ? 'Signed PDF' : 'PDF'}
            </Button>
          </Tooltip>
          {record.esign?.signedDocumentUrl && record.pdfUrl && (
            <Tooltip title="View the unsigned original">
              <Button
                type="link"
                size="small"
                icon={<FilePdfOutlined />}
                onClick={() => window.open(record.pdfUrl, '_blank')}
              >
                Original
              </Button>
            </Tooltip>
          )}
          {record.esign?.auditTrailUrl && (
            <Tooltip title="Aadhaar eSign audit trail">
              <Button
                type="link"
                size="small"
                icon={<SafetyOutlined />}
                onClick={() => window.open(record.esign.auditTrailUrl, '_blank')}
              >
                Audit Trail
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Regenerate from snapshot">
            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              loading={regenerating === record.agreementId}
              onClick={() => handleRegenerate(record.agreementId)}
            >
              Regenerate
            </Button>
          </Tooltip>
          {record.esign?.status === 'completed' && (!record.esign?.signedDocumentUrl || !record.esign?.auditTrailUrl) && (
            <Tooltip title="Signed at the provider, but the file was not saved here. Retry the download.">
              <Button
                type="link"
                size="small"
                icon={<ReloadOutlined />}
                loading={eSignLoading}
                onClick={() => handleESignFinalize(record.agreementId)}
              >
                {record.esign?.signedDocumentUrl ? 'Get Audit Trail' : 'Get Signed Copy'}
              </Button>
            </Tooltip>
          )}
          {record.esign?.status === 'started' && record.esign?.signUrl ? (
            <Tooltip title="Open signing page (eSign pending)">
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => {
                  setESignModal({
                    visible: true,
                    agreementId: record.agreementId,
                    signUrl: record.esign.signUrl,
                    requestId: record.esign.requestId,
                    documentId: record.esign.documentId,
                  });
                }}
              >
                Open
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title={record.esign?.status === 'completed' ? 'Already signed' : 'Send for eSign'}>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                disabled={!record.pdfUrl || record.esign?.status === 'completed'}
                loading={eSignLoading && eSignModal.agreementId === record.agreementId}
                onClick={() => handleESignStart(record.agreementId)}
              >
                eSign
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      {modalContextHolder}

      {/* ── Generate Buttons ── */}
      <Row gutter={[16, 16]} className="mb-3">
        {/* PRE_LOAN_TAKEOVER */}
        {(!filterType || filterType === 'PRE_LOAN_TAKEOVER') && (
        <Col xs={24} md={filterType ? 24 : 12}>
          <Card
            size="small"
            bordered
            style={{ background: existingPreLoan ? '#f0f5ff' : undefined }}
          >
            <Row justify="space-between" align="middle">
              <Col>
                <Space>
                  <AuditOutlined style={{ fontSize: 20, color: '#1677ff' }} />
                  <div>
                    <Text strong>Pre-Loan Takeover Agreement</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {existingPreLoan
                        ? `${existingPreLoan.agreementId} (v${existingPreLoan.version})`
                        : 'Not generated yet'}
                    </Text>
                  </div>
                </Space>
              </Col>
              <Col>
                {existingPreLoan ? (
                  <Space>
                    <Button
                      size="small"
                      icon={<FilePdfOutlined />}
                      onClick={() => {
                        const url = existingPreLoan.esign?.signedDocumentUrl || existingPreLoan.pdfUrl;
                        if (url) window.open(url, '_blank');
                      }}
                    >
                      {existingPreLoan.esign?.signedDocumentUrl ? 'View Signed' : 'View'}
                    </Button>
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      loading={regenerating === existingPreLoan.agreementId}
                      onClick={() => handleRegenerate(existingPreLoan.agreementId)}
                    >
                      Regen
                    </Button>
                    {existingPreLoan.esign?.status === 'started' && existingPreLoan.esign?.signUrl ? (
                      <Button
                        size="small"
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={() => {
                          setESignModal({
                            visible: true,
                            agreementId: existingPreLoan.agreementId,
                            signUrl: existingPreLoan.esign.signUrl,
                            requestId: existingPreLoan.esign.requestId,
                            documentId: existingPreLoan.esign.documentId,
                          });
                        }}
                      >
                        Open
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        disabled={existingPreLoan.esign?.status === 'completed'}
                        loading={eSignLoading && eSignModal.agreementId === existingPreLoan.agreementId}
                        onClick={() => handleESignStart(existingPreLoan.agreementId)}
                      >
                        {existingPreLoan.esign?.status === 'completed' ? 'Signed' : 'eSign'}
                      </Button>
                    )}
                  </Space>
                ) : (
                  <Button
                    type="primary"
                    size="small"
                    icon={<FileTextOutlined />}
                    disabled={!canPreLoan}
                    loading={previewing && previewType === 'PRE_LOAN_TAKEOVER'}
                    onClick={() => handlePreview('PRE_LOAN_TAKEOVER')}
                  >
                    Generate
                  </Button>
                )}
              </Col>
            </Row>
            {!canPreLoan && !existingPreLoan && (
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                Available at: {PRE_LOAN_ALLOWED.map((s) => s.replace(/_/g, ' ')).join(', ')}
              </Text>
            )}
          </Card>
        </Col>
        )}

        {/* FINAL_PURCHASE */}
        {(!filterType || filterType === 'FINAL_PURCHASE') && (
        <Col xs={24} md={filterType ? 24 : 12}>
          <Card
            size="small"
            bordered
            style={{ background: existingFinalPurchase ? '#f6ffed' : undefined }}
          >
            <Row justify="space-between" align="middle">
              <Col>
                <Space>
                  <SafetyOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                  <div>
                    <Text strong>Final Purchase Agreement</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {existingFinalPurchase
                        ? `${existingFinalPurchase.agreementId} (v${existingFinalPurchase.version})`
                        : 'Not generated yet'}
                    </Text>
                  </div>
                </Space>
              </Col>
              <Col>
                {existingFinalPurchase ? (
                  <Space>
                    <Button
                      size="small"
                      icon={<FilePdfOutlined />}
                      onClick={() => {
                        const url = existingFinalPurchase.esign?.signedDocumentUrl || existingFinalPurchase.pdfUrl;
                        if (url) window.open(url, '_blank');
                      }}
                    >
                      {existingFinalPurchase.esign?.signedDocumentUrl ? 'View Signed' : 'View'}
                    </Button>
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      loading={regenerating === existingFinalPurchase.agreementId}
                      onClick={() => handleRegenerate(existingFinalPurchase.agreementId)}
                    >
                      Regen
                    </Button>
                    {existingFinalPurchase.esign?.status === 'started' && existingFinalPurchase.esign?.signUrl ? (
                      <Button
                        size="small"
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={() => {
                          setESignModal({
                            visible: true,
                            agreementId: existingFinalPurchase.agreementId,
                            signUrl: existingFinalPurchase.esign.signUrl,
                            requestId: existingFinalPurchase.esign.requestId,
                            documentId: existingFinalPurchase.esign.documentId,
                          });
                        }}
                      >
                        Open
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        disabled={existingFinalPurchase.esign?.status === 'completed'}
                        loading={eSignLoading && eSignModal.agreementId === existingFinalPurchase.agreementId}
                        onClick={() => handleESignStart(existingFinalPurchase.agreementId)}
                      >
                        {existingFinalPurchase.esign?.status === 'completed' ? 'Signed' : 'eSign'}
                      </Button>
                    )}
                  </Space>
                ) : (
                  <Button
                    type="primary"
                    size="small"
                    icon={<FileTextOutlined />}
                    disabled={!canFinalPurchase}
                    loading={previewing && previewType === 'FINAL_PURCHASE'}
                    onClick={() => handlePreview('FINAL_PURCHASE')}
                    style={canFinalPurchase ? { background: '#52c41a', borderColor: '#52c41a' } : {}}
                  >
                    Generate
                  </Button>
                )}
              </Col>
            </Row>
            {!canFinalPurchase && !existingFinalPurchase && (
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                {!goldReceipt
                  ? 'Requires Gold Receipt + Valuation Completed status'
                  : `Available at: ${FINAL_PURCHASE_ALLOWED.map((s) => s.replace(/_/g, ' ')).join(', ')}`}
              </Text>
            )}
          </Card>
        </Col>
        )}
      </Row>

      {/* ── Agreements Table ── */}
      {(() => {
        const filtered = filterType
          ? agreements.filter((a) => a.agreementType === filterType)
          : agreements;
        return filtered.length > 0 ? (
        <Spin spinning={loading}>
          <Table
            dataSource={filtered.map((a) => ({ ...a, key: a._id || a.agreementId }))}
            columns={columns}
            pagination={false}
            size="small"
            bordered
            style={{ marginTop: 8 }}
          />
        </Spin>
      ) : !loading ? (
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <Text type="secondary">No agreements generated yet{filterType ? ` (${TYPE_LABELS[filterType] || filterType})` : ''} for this case.</Text>
        </div>
      ) : null;
      })()}

      {/* ── Preview Modal ── */}
      <Modal
        title={
          <Space>
            <FilePdfOutlined />
            <span>Agreement Preview — {TYPE_LABELS[previewType] || previewType}</span>
          </Space>
        }
        open={previewOpen}
        onCancel={() => {
          setPreviewOpen(false);
          setPreviewPdf(null);
        }}
        width={900}
        centered
        footer={[
          <Button key="cancel" onClick={() => { setPreviewOpen(false); setPreviewPdf(null); }}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={confirming}
            onClick={handleConfirm}
          >
            Confirm & Save Agreement
          </Button>,
        ]}
        destroyOnClose
      >
        {previewPdf ? (
          <div style={{ height: '70vh', width: '100%' }}>
            <iframe
              src={`data:application/pdf;base64,${previewPdf}`}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Agreement Preview"
            />
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <Spin size="large" />
            <br />
            <Text type="secondary">Generating preview…</Text>
          </div>
        )}
      </Modal>

      {/* ── eSign Modal: completes on its own via webhook / signer return ── */}
      <Modal
        title="Aadhaar eSign"
        open={eSignModal.visible}
        onOk={handleESignFinalize}
        onCancel={handleESignCancel}
        okText="Check status now"
        cancelText="Close"
        okButtonProps={{ type: 'default' }}
        confirmLoading={eSignLoading}
        closable={!eSignLoading}
        maskClosable={false}
        keyboard={false}
        width={720}
      >
        <div style={{ padding: '8px 0' }}>
          {watchedStatus === 'failed' ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="Signing was not completed"
              description={watchedAgreement?.esign?.metadata?.webhook?.error || 'The signer rejected the document or the invitation expired. Close this and start eSign again.'}
            />
          ) : (
            <Space align="start" style={{ marginBottom: 12 }}>
              <Spin size="small" />
              <Typography.Text>
                Waiting for the customer to sign on Leegality. This closes by itself and saves the signed
                agreement the moment signing completes.
              </Typography.Text>
            </Space>
          )}
          {eSignWatcher.popupBlocked && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="The browser blocked the signing window — use “Open Signing Page”." />
          )}
          {eSignWatcher.popupClosed && !watchedStatus?.match(/completed|failed/) && (
            <Alert type="info" showIcon style={{ marginBottom: 12 }} message="The signing window was closed. If the customer finished signing, this updates automatically; otherwise reopen the signing page." />
          )}
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <Button block type="primary" onClick={() => eSignWatcher.open(eSignModal.signUrl)} disabled={!eSignModal.signUrl}>
                Open Signing Page
              </Button>
            </Col>
            <Col span={12}>
              <Button
                block
                onClick={() => {
                  if (eSignModal.signUrl) {
                    navigator.clipboard.writeText(eSignModal.signUrl);
                    messageApi.success('Signing link copied!');
                  }
                }}
                disabled={!eSignModal.signUrl}
              >
                Copy Signing Link
              </Button>
            </Col>
          </Row>
        </div>
      </Modal>
    </>
  );
};

export default GoldAgreementSection;
